import { apiFetch, readJson, readJsonArray, readErrorMessage } from './client';
import type {
  ConnectionTestResult,
  CreateMountRequest,
  DirectoryPrincipal,
  StorageMountDetailDto,
  StorageMountDto,
  TestMountConnectionRequest,
  UpdateMountRequest
} from './storageMountsAdmin';

export interface ClientPortalTenantOption {
  tenantId: string;
  displayName: string;
}

export interface ClientGraphTenantStatus {
  tenantId: string;
  displayName: string;
  hasClientId: boolean;
  hasClientSecret: boolean;
  credentialsComplete: boolean;
  probeSucceeded: boolean | null;
  statusMessage: string;
}

export interface ClientGraphReadinessReport {
  tenants: ClientGraphTenantStatus[];
  selectedTenant: ClientGraphTenantStatus | null;
}

function qs(params: Record<string, string | undefined>): string {
  const parts = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (parts.length === 0) return '';
  return `?${parts.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`).join('&')}`;
}

function tenantBase(tenantId: string): string {
  return `/api/admin/client-portal/tenants/${encodeURIComponent(tenantId)}/storage-mounts`;
}

export async function listClientPortalTenants(): Promise<ClientPortalTenantOption[]> {
  const res = await apiFetch('/api/admin/client-portal/tenants');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<ClientPortalTenantOption>(res);
}

export async function listClientPortalMounts(tenantId: string): Promise<StorageMountDto[]> {
  const res = await apiFetch(tenantBase(tenantId));
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<StorageMountDto>(res);
}

export async function getClientPortalMount(tenantId: string, id: string): Promise<StorageMountDetailDto> {
  const res = await apiFetch(`${tenantBase(tenantId)}/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<StorageMountDetailDto>(res);
}

export async function createClientPortalMount(
  tenantId: string,
  req: CreateMountRequest
): Promise<StorageMountDto> {
  const res = await apiFetch(tenantBase(tenantId), {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<StorageMountDto>(res);
}

export async function updateClientPortalMount(
  tenantId: string,
  id: string,
  req: UpdateMountRequest
): Promise<StorageMountDto> {
  const res = await apiFetch(`${tenantBase(tenantId)}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<StorageMountDto>(res);
}

export async function deleteClientPortalMount(tenantId: string, id: string): Promise<void> {
  const res = await apiFetch(`${tenantBase(tenantId)}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error(await readErrorMessage(res));
}

export async function testClientPortalMount(tenantId: string, id: string): Promise<ConnectionTestResult> {
  const res = await apiFetch(`${tenantBase(tenantId)}/${encodeURIComponent(id)}/test`, { method: 'POST' });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ConnectionTestResult>(res);
}

export async function testClientPortalMountConnection(
  tenantId: string,
  req: TestMountConnectionRequest
): Promise<ConnectionTestResult> {
  const res = await apiFetch(`${tenantBase(tenantId)}/test-connection`, {
    method: 'POST',
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ConnectionTestResult>(res);
}

export async function listClientPortalMountRoles(tenantId: string): Promise<string[]> {
  const res = await apiFetch(`${tenantBase(tenantId)}/roles`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<string>(res);
}

export async function searchClientPortalGroups(tenantId: string, q: string): Promise<DirectoryPrincipal[]> {
  const res = await apiFetch(`${tenantBase(tenantId)}/groups/search${qs({ q })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<DirectoryPrincipal>(res);
}

export async function getClientPortalGraphReadiness(
  tenantId: string,
  probe = true
): Promise<ClientGraphReadinessReport> {
  const res = await apiFetch(`${tenantBase(tenantId)}/graph/status${qs({ probe: probe ? 'true' : 'false' })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<ClientGraphReadinessReport>(res);
}

export function createClientPortalStorageMountsApi(tenantId: string) {
  return {
    listMounts: () => listClientPortalMounts(tenantId),
    getMount: (id: string) => getClientPortalMount(tenantId, id),
    createMount: (req: CreateMountRequest) => createClientPortalMount(tenantId, req),
    updateMount: (id: string, req: UpdateMountRequest) => updateClientPortalMount(tenantId, id, req),
    deleteMount: (id: string) => deleteClientPortalMount(tenantId, id),
    testMount: (id: string) => testClientPortalMount(tenantId, id),
    testMountConnection: (req: TestMountConnectionRequest) => testClientPortalMountConnection(tenantId, req),
    listRoles: () => listClientPortalMountRoles(tenantId),
    searchGroups: (q: string) => searchClientPortalGroups(tenantId, q)
  };
}
